/**
 * The sport-keyed player-prop stat registry (nfl-player-props, 2026-09-03).
 *
 * `MlbPropStat` was the only prop stat type in the repo, and the whole prop
 * pipeline — `MlbPropStatGroup`, `buildPlayerPropSummaries`, the `|prop-`
 * synth key, the workbench adapter — was typed to it. NFL's per-game player
 * series share the same shape (yes-ladder rungs keyed game → stat → player →
 * threshold; hydracore stamps ONE `class: 'player-prop'` + `stat` vocabulary
 * for both sports), so the pipeline is widened to a union rather than cloned.
 *
 * ONE TABLE (nba-player-props, 2026-10-21). `PROP_VOCABULARY` holds every
 * sport's ids, chip order, labels and no-line families, and — for the sports
 * whose rows the WIRE selects — the (wire stat → id) and (Kalshi series → id)
 * maps. Every function below reads a sport only through that table, so a
 * sport joins the prop pipeline by adding an entry, never a branch (Dan:
 * consumer behaviour is one mechanism for all sports). The per-sport modules
 * (`sports/mlb.ts`, `nfl.ts`, `nhl.ts`, `nba.ts`) stay the authorities for
 * their own data; `MlbPropStat` is left in place so its importers keep
 * compiling unchanged.
 *
 * Pure: no store, no fetch, no clock.
 */
import {
  MLB_PROP_STATS,
  MLB_PROP_STAT_LABELS,
  mlbPropRungLabels,
  type MlbPropStat,
} from '../kalshiTicker/sports/mlb';
import {
  NBA_PROP_BINARY_THRESHOLD,
  NBA_PROP_BINARY_WORDING,
  NBA_PROP_SERIES,
  NBA_PROP_STATS,
  NBA_PROP_STAT_LABELS,
  NBA_WIRE_PROP_STAT,
  type NbaPropStat,
} from '../kalshiTicker/sports/nba';
import {
  NFL_PROP_BINARY_THRESHOLD,
  NFL_PROP_SERIES,
  NFL_PROP_STATS,
  NFL_PROP_STAT_LABELS,
  nflPropRungLabels,
  type NflPropStat,
} from '../kalshiTicker/sports/nfl';
import {
  NHL_PROP_BINARY_THRESHOLD,
  NHL_PROP_BINARY_WORDING,
  NHL_PROP_SERIES,
  NHL_PROP_STATS,
  NHL_PROP_STAT_LABELS,
  NHL_WIRE_PROP_STAT,
  type NhlPropStat,
} from '../kalshiTicker/sports/nhl';

import {
  SOCCER_PROP_BINARY_THRESHOLD,
  SOCCER_PROP_STATS,
  SOCCER_PROP_STAT_LABELS,
  soccerPropRungLabels,
  type SoccerPropStat,
} from '../kalshiTicker/sports/soccer';
export type { MlbPropStat, NbaPropStat, NflPropStat, NhlPropStat, SoccerPropStat };

/** Every player-prop stat id, all sports. The vocabularies are disjoint
 *  (MLB `hits hr rbi tb sb ks outs hrr`; NFL `pass-yds … dst-td`; NHL
 *  `nhl-goals … nhl-first-goal`; NBA `nba-pts … nba-pts-leader`), which is
 *  what lets a stat id stand alone in a synth key without a sport tag. */
export type PropStat = MlbPropStat | NflPropStat | NhlPropStat | NbaPropStat | SoccerPropStat;

/** Every sport with a player-prop pipeline — the sport a `PropStat` belongs to. */
export type PropStatSport = 'mlb' | 'nfl' | 'nhl' | 'nba' | 'soccer';

/** Sports with a live DAY-WIDE prop pipeline (PropWorkbench). NHL and NBA
 *  props are Game Detail only, so they are `PropStatSport`s but not
 *  `PropSport`s. Soccer (goalscorer props, 2026-09-09) is on this lane's
 *  workbench. */
export type PropSport = 'mlb' | 'nfl' | 'soccer';

export const PROP_SPORTS: readonly PropSport[] = ['mlb', 'nfl', 'soccer'];

type RungLabels = { label: string; notLabel: string };

/** One sport's player-prop vocabulary — what the pipeline knows about it. */
interface PropVocabulary<S extends PropStat> {
  /** Every id, in stat-chip order. */
  stats: readonly S[];
  labels: Readonly<Record<S, string>>;
  /** The no-line families (yes/no per player): each carries a FIXED rung
   *  threshold as identity — its own "N+" reading — so the numeric
   *  `PropRung` / synth-key contract holds without a null branch. */
  binary: Readonly<Partial<Record<S, number>>>;
  /** Wire `stat` (lowercase, hydracore HYDRA_API.md § Player props) → id, on
   *  the sports whose rows the WIRE selects (`class: 'player-prop'`). Absent
   *  on MLB, which classifies by ticker + the Poly join. */
  wire?: Readonly<Record<string, S>>;
  /** Kalshi series (the exact segment before the first dash) → id: what a
   *  ticker alone says before its frame arrives — a held position, a deep
   *  link, the account plane's feed opt-in. */
  series?: Readonly<Record<string, S>>;
  /** Each no-line family's YES / NO, worded after the player's name. */
  binaryWording?: Readonly<Partial<Record<S, { yes: string; no: string }>>>;
  /** The sport's own rung wording, where the shared wording cannot say it
   *  (NFL's team-less NONE leg). */
  rungLabels?: (playerName: string, teamCode: string | null | undefined, stat: S, threshold: number, playerKey?: string) => RungLabels;
}

/** Widen one sport's typed entry into the table. */
const vocabulary = <S extends PropStat>(v: PropVocabulary<S>): PropVocabulary<PropStat> =>
  v as unknown as PropVocabulary<PropStat>;

/** The one table. A sport joins by adding an entry here. */
export const PROP_VOCABULARY: Readonly<Record<PropStatSport, PropVocabulary<PropStat>>> = {
  mlb: vocabulary<MlbPropStat>({
    stats: MLB_PROP_STATS,
    labels: MLB_PROP_STAT_LABELS,
    binary: {},
    rungLabels: mlbPropRungLabels,
  }),
  nfl: vocabulary<NflPropStat>({
    stats: NFL_PROP_STATS,
    labels: NFL_PROP_STAT_LABELS,
    binary: NFL_PROP_BINARY_THRESHOLD,
    // The NFL wire spells its stats as g1's own ids, verbatim.
    wire: Object.fromEntries(NFL_PROP_STATS.map((s) => [s, s])),
    series: NFL_PROP_SERIES,
    rungLabels: nflPropRungLabels,
  }),
  nhl: vocabulary<NhlPropStat>({
    stats: NHL_PROP_STATS,
    labels: NHL_PROP_STAT_LABELS,
    binary: NHL_PROP_BINARY_THRESHOLD,
    wire: NHL_WIRE_PROP_STAT,
    series: NHL_PROP_SERIES,
    binaryWording: NHL_PROP_BINARY_WORDING,
  }),
  nba: vocabulary<NbaPropStat>({
    stats: NBA_PROP_STATS,
    labels: NBA_PROP_STAT_LABELS,
    binary: NBA_PROP_BINARY_THRESHOLD,
    wire: NBA_WIRE_PROP_STAT,
    series: NBA_PROP_SERIES,
    binaryWording: NBA_PROP_BINARY_WORDING,
  }),
  // soccer-goalscorer-props (2026-09-09): goals ladder + first goalscorer.
  // The wire spells the stats as g1's own ids (`goal`, `first-goal`).
  soccer: vocabulary<SoccerPropStat>({
    stats: SOCCER_PROP_STATS,
    labels: SOCCER_PROP_STAT_LABELS,
    binary: SOCCER_PROP_BINARY_THRESHOLD,
    wire: Object.fromEntries(SOCCER_PROP_STATS.map((s) => [s, s])),
    rungLabels: soccerPropRungLabels,
  }),
};

const PROP_STAT_SPORTS = Object.keys(PROP_VOCABULARY) as PropStatSport[];

const hasOwn = (o: object, key: string): boolean => Object.prototype.hasOwnProperty.call(o, key);

export const PROP_STATS_BY_SPORT: Record<PropStatSport, readonly PropStat[]> = Object.fromEntries(
  PROP_STAT_SPORTS.map((sport) => [sport, PROP_VOCABULARY[sport].stats]),
) as Record<PropStatSport, readonly PropStat[]>;

/** Every stat id, sport by sport (table order), each in its own display order. */
export const PROP_STATS: readonly PropStat[] = PROP_STAT_SPORTS.flatMap((sport) => PROP_VOCABULARY[sport].stats);

export const PROP_STAT_LABELS: Record<PropStat, string> = Object.assign(
  {},
  ...PROP_STAT_SPORTS.map((sport) => PROP_VOCABULARY[sport].labels),
) as Record<PropStat, string>;

const SPORT_OF_STAT: ReadonlyMap<string, PropStatSport> = new Map(
  PROP_STAT_SPORTS.flatMap((sport) => PROP_VOCABULARY[sport].stats.map((stat) => [stat, sport] as const)),
);

/** Kalshi series → id across every sport that lists its series. */
const STAT_OF_SERIES: ReadonlyMap<string, PropStat> = new Map(
  PROP_STAT_SPORTS.flatMap((sport) => Object.entries(PROP_VOCABULARY[sport].series ?? {})),
);

/** Sports that catalogue two native series for one stat (NHL SAVE / SAVES,
 *  NBA STOCK / STOCKS), so one ladder can list two contracts at a threshold. */
const TWIN_SERIES_SPORTS: ReadonlySet<PropStatSport> = new Set(
  PROP_STAT_SPORTS.filter((sport) => {
    const ids = Object.values(PROP_VOCABULARY[sport].series ?? {});
    return new Set(ids).size < ids.length;
  }),
);

export function isPropStat(value: string): value is PropStat {
  return SPORT_OF_STAT.has(value);
}

export function isPropStatSport(value: string): value is PropStatSport {
  return hasOwn(PROP_VOCABULARY, value);
}

export function isMlbPropStat(value: string): value is MlbPropStat {
  return SPORT_OF_STAT.get(value) === 'mlb';
}

export function isNflPropStat(value: string): value is NflPropStat {
  return SPORT_OF_STAT.get(value) === 'nfl';
}

export function isNhlPropStat(value: string): value is NhlPropStat {
  return SPORT_OF_STAT.get(value) === 'nhl';
}

export function isSoccerPropStat(value: string): value is SoccerPropStat {
  return SPORT_OF_STAT.get(value) === 'soccer';
}

export function propStatSport(stat: PropStat): PropStatSport {
  return SPORT_OF_STAT.get(stat)!;
}

/** Does the WIRE select this sport's prop rows (a `wire` vocabulary)? MLB's
 *  are still classified by ticker + the Poly join. */
export function isWirePropSport(sport: PropStatSport): boolean {
  return PROP_VOCABULARY[sport].wire !== undefined;
}

/** (base sport, wire stat) → g1 id, or null for a stat the sport's table does
 *  not list — ignored, never guessed (rule 8). */
export function wirePropStat(sport: PropStatSport, wireStat: string): PropStat | null {
  const table = PROP_VOCABULARY[sport].wire;
  const key = wireStat.trim().toLowerCase();
  return table && hasOwn(table, key) ? table[key] : null;
}

/** A Kalshi ticker's prop stat by its EXACT series segment, or null. Says only
 *  "this series is that stat"; the player and rung come from the live frame. */
export function propStatOfKalshiSeries(ticker: string): PropStat | null {
  const i = ticker.indexOf('-');
  return STAT_OF_SERIES.get((i > 0 ? ticker.slice(0, i) : ticker).trim().toUpperCase()) ?? null;
}

/** See `TWIN_SERIES_SPORTS`. */
export function propSportHasTwinSeries(sport: string): boolean {
  return TWIN_SERIES_SPORTS.has(sport as PropStatSport);
}

/** Position of a stat within ITS SPORT's display order — the category order
 *  the day-wide slate model sorts player groups by. */
export function propStatOrder(stat: PropStat): number {
  return PROP_STATS_BY_SPORT[propStatSport(stat)].indexOf(stat);
}

/** A stat whose markets are structured yes/no per player rather than a
 *  threshold ladder: its FIXED identity threshold, else null. */
export function propBinaryThreshold(stat: PropStat): number | null {
  return PROP_VOCABULARY[propStatSport(stat)].binary[stat] ?? null;
}

export function isBinaryPropStat(stat: PropStat): boolean {
  return propBinaryThreshold(stat) !== null;
}

/** The rung without the player — "6+ Strikeouts", "350+ Passing Yards", or
 *  the family name for a no-line binary ("1st Touchdown", "Double Double"). */
export function propRungLabel(stat: PropStat, threshold: number): string {
  return isBinaryPropStat(stat) ? PROP_STAT_LABELS[stat] : `${threshold}+ ${PROP_STAT_LABELS[stat]}`;
}

/** The ladder's threshold tag — "6+" on a ladder, "Yes/No" on a binary. */
export function propRungTag(stat: PropStat, threshold: number): string {
  return isBinaryPropStat(stat) ? 'Yes/No' : `${threshold}+`;
}

/** The two sides of ONE prop rung (#251): YES names the outcome, NO is its
 *  complement, both worded from the same parts. A sport's own wording wins
 *  where it has one; otherwise a binary reads from the table's wording and a
 *  ladder reads "N+ Stat" / "does not get N+ Stat". `playerKey` lets the NFL
 *  wording recognise the reserved `NONE` leg. */
export function propRungLabels(
  playerName: string,
  teamCode: string | null | undefined,
  stat: PropStat,
  threshold: number,
  playerKey?: string,
): RungLabels {
  const vocab = PROP_VOCABULARY[propStatSport(stat)];
  if (vocab.rungLabels) return vocab.rungLabels(playerName, teamCode, stat, threshold, playerKey);
  const team = teamCode ? ` (${teamCode})` : '';
  const who = `${playerName}${team}`;
  const binary = vocab.binaryWording?.[stat];
  if (binary) return { label: `${who} ${binary.yes}`, notLabel: `${who} ${binary.no}` };
  const rung = propRungLabel(stat, threshold);
  return { label: `${who} ${rung}`, notLabel: `${who} does not get ${rung}` };
}
